import React, { useMemo, useState } from 'react';
import { BabyIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { getMaternityWardStage } from '../maternity/maternityWard';
type ActiveMaternityListProps = {
  onRegister: () => void;
};
export function ActiveMaternityList({
  onRegister
}: ActiveMaternityListProps) {
  const {
    maternityFiles
  } = useData();
  const [search, setSearch] = useState('');

  // Active = open maternity file (not completed/discharged) currently in labour.
  const active = useMemo(() => maternityFiles.filter(file => file.stage === 'NURSING' && !file.dischargeSlip?.dateOfDischarge && getMaternityWardStage(file) === 'labour').sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()), [maternityFiles]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return active;
    return active.filter(file => `${file.patient?.firstName || ''} ${file.patient?.surname || ''} ${file.patient?.otherNames || ''} ${file.bmcFileNumber} ${file.patient?.phone || ''}`.toLowerCase().includes(q));
  }, [active, search]);
  return <Card title="Active Maternity Patients" subtitle={`${active.length} currently in labour`} action={<Button size="sm" onClick={onRegister} className="bg-pink-600 hover:bg-pink-700 focus:ring-pink-500" data-id="element-5860" data-mp="1znhv6gnhmt5v">
          <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-5861" data-mp="1vosk4upx9npe" /> Register maternity patient
        </Button>} data-id="element-5859" data-mp="24o5mz4jjje15">
      <div className="relative mb-4" data-id="element-5862" data-mp="3q0yx9z2dmekc">
        <label htmlFor="maternity-active-search" className="sr-only" data-id="element-5863" data-mp="3pzxlfsdjdo1k">
          Search active maternity patients
        </label>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-5864" data-mp="3l2nnfoc30424" />
        <input id="maternity-active-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, file no. or phone" className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-5865" data-mp="7za4ort116po" />
      </div>

      {visible.length === 0 ? <div className="py-12 text-center text-gray-500" data-id="element-5866" data-mp="vr7g4hskdwq9">
          <BabyIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" data-id="element-5867" data-mp="vol7sfxund6z" />
          <p className="text-sm" data-id="element-5868" data-mp="hq4v88db2sgx">
            {active.length === 0 ? 'No maternity patients are currently in labour.' : `No active patients match "${search}".`}
          </p>
        </div> : <ul className="divide-y divide-gray-100" data-id="element-5869" data-mp="32j8aho344def">
          {visible.map(file => {
        const observations = file.labourObservations || [];
        const latest = observations[observations.length - 1];
        const labourStart = file.delivery?.firstStageBeganDate || observations[0]?.date;
        return <li key={file.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4" data-id="element-5870" data-mp="38xnkey90zdsb">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pink-100 text-pink-600" data-id="element-5871" data-mp="33w82hf259ne5">
                  <BabyIcon className="h-4 w-4" data-id="element-5872" data-mp="2c7en1jjkge5m" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-5873" data-mp="2c51xak7kgzi0">
                  <p className="truncate font-medium text-gray-900" data-id="element-5874" data-mp="2pyt8ry4uytgd">
                    {file.patient?.firstName} {file.patient?.surname}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-5875" data-mp="1xi8olxsjyrys">
                    {file.bmcFileNumber}
                    {file.patient?.age ? ` · ${file.patient.age} yrs` : ''}
                    {file.patient?.phone ? ` · ${file.patient.phone}` : ''}
                  </p>
                </div>
                <div className="text-xs text-gray-600 sm:text-right" data-id="element-5876" data-mp="2641leg57a179">
                  {labourStart && <p data-id="element-5877" data-mp="2g428md66x1gq">
                      Labour since{' '}
                      {new Date(labourStart).toLocaleDateString()}
                    </p>}
                  {latest?.cervicalDilation && <p data-id="element-5878" data-mp="2ox5gm5y2gm3z">Last dilation: {latest.cervicalDilation} cm</p>}
                </div>
                <span className="inline-flex w-fit rounded-full bg-pink-100 px-2.5 py-1 text-xs font-semibold text-pink-800" data-id="element-5879" data-mp="2tufe88vm042w">
                  In labour
                </span>
              </li>;
      })}
        </ul>}
    </Card>;
}